import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Button } from "./button.js";
import { SelectField } from "./select-field.js";
import { SpecularButton } from "./specular-button.js";

// The list footer every paginated page in the app uses: "Showing 21–40 of 57",
// numbered pages, Prev / Next, and an optional rows-per-page picker.
//
// `meta` is the `{ total, page, limit, totalPages }` envelope every list
// endpoint returns. Pass `onPageSizeChange` to show the rows-per-page picker.

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginationProps {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  /** Shows the "Rows per page" picker when given. */
  onPageSizeChange?: ((limit: number) => void) | undefined;
  pageSizeOptions?: number[] | undefined;
  className?: string | undefined;
}

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Page numbers to show, with "…" gaps: 1 … 4 5 [6] 7 8 … 40. */
export function pageWindow(page: number, totalPages: number): (number | "…")[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (page >= totalPages - 2) [totalPages - 1, totalPages - 2, totalPages - 3].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  sorted.forEach((p, i) => {
    const prev = sorted[i - 1];
    if (prev !== undefined && p - prev > 1) out.push("…");
    out.push(p);
  });
  return out;
}

function Pagination({ meta, onPageChange, onPageSizeChange, pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS, className }: PaginationProps) {
  if (meta.total === 0) return null;

  const totalPages = Math.max(1, meta.totalPages);
  const from = (meta.page - 1) * meta.limit + 1;
  const to = Math.min(meta.page * meta.limit, meta.total);
  const sizes = pageSizeOptions.includes(meta.limit) ? pageSizeOptions : [...pageSizeOptions, meta.limit].sort((a, b) => a - b);

  return (
    <div className={cn("mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 text-[13px] text-surface-700", className)}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span>
          Showing <strong className="font-semibold text-surface-900">{from}–{to}</strong> of{" "}
          <strong className="font-semibold text-surface-900">{meta.total}</strong>
        </span>
        {onPageSizeChange && (
          <label className="flex items-center gap-2 text-[12px] text-surface-500">
            Rows per page
            <SelectField
              size="sm"
              className="w-[76px]"
              aria-label="Rows per page"
              value={meta.limit}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              {sizes.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </SelectField>
          </label>
        )}
      </div>

      {/* Always shown (disabled on a single page) so every list reads the same. */}
      <nav aria-label="Pagination" className="flex items-center gap-1.5">
        <SpecularButton disabled={meta.page <= 1} onClick={() => onPageChange(meta.page - 1)}>
          <ChevronLeft className="h-4 w-4" aria-hidden /> Prev
        </SpecularButton>
        <div className="hidden items-center gap-1 sm:flex">
          {pageWindow(meta.page, totalPages).map((p, i) =>
            p === "…" ? (
              <span key={`gap-${i}`} className="px-1 text-surface-400" aria-hidden>…</span>
            ) : (
              <Button
                key={p}
                size="sm"
                variant={p === meta.page ? "default" : "ghost"}
                className="min-w-8 px-2"
                aria-label={`Page ${p}`}
                aria-current={p === meta.page ? "page" : undefined}
                onClick={() => p !== meta.page && onPageChange(p)}
              >
                {p}
              </Button>
            ),
          )}
        </div>
        <span className="px-2 text-[12px] text-surface-500 sm:hidden">
          {meta.page} / {totalPages}
        </span>
        <SpecularButton disabled={meta.page >= totalPages} onClick={() => onPageChange(meta.page + 1)}>
          Next <ChevronRight className="h-4 w-4" aria-hidden />
        </SpecularButton>
      </nav>
    </div>
  );
}

export { Pagination };
